import { DEVELLO_API_ORIGIN, fetchWithAuth } from '../utils/asyncReliability';
import { devLog, devError } from '../utils/logger';
import { supabase } from '../supabase';

// Email notification service for team assignments
export const sendAssignmentNotification = async (assignmentData, teamMember, project) => {
  try {
    const emailContent = {
      to: teamMember.email,
      subject: `New Assignment: ${project.name}`,
      html: generateAssignmentEmailHTML(assignmentData, teamMember, project),
      text: generateAssignmentEmailText(assignmentData, teamMember, project)
    };
    
    // Send email using your existing SendGrid setup
    const apiUrl = `${DEVELLO_API_ORIGIN}/api/send-assignment-email`;
    
    devLog('📧 Attempting to send email to:', apiUrl);
    devLog('📧 Request payload:', { to: teamMember.email, subject: `New Assignment: ${project.name}` });
      
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: teamMember.email,
        subject: `New Assignment: ${project.name}`,
        html: emailContent.html,
        text: emailContent.text
      }),
      name: 'email:assignment',
      timeoutMs: 45000,
      retries: 1,
      supabaseClient: supabase,
      requireAuth: false,
      retryAuth: true,
    });

    if (!response.ok) {
      throw new Error(response.userMessage || 'Email sending failed');
    }
    const result = response.data;
    devLog('Assignment notification sent:', result);
    
    return { success: true, message: 'Email notification sent successfully' };
  } catch (error) {
    devError('Error sending assignment notification:', error);
    return { success: false, message: 'Failed to send email notification' };
  }
};

const generateAssignmentEmailHTML = (assignmentData, teamMember, project) => {
  const startDate = new Date(assignmentData.startDate).toLocaleDateString();
  const endDate = new Date(assignmentData.endDate).toLocaleDateString();
  const workDays = assignmentData.workDays?.map(day => 
    new Date(day).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    })
  ).join(', ') || 'All days';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #2563eb; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 20px; border-radius: 0 0 8px 8px; }
        .assignment-details { background: white; padding: 15px; margin: 15px 0; border-radius: 5px; border-left: 4px solid #2563eb; }
        .highlight { background: #dbeafe; padding: 10px; border-radius: 5px; margin: 10px 0; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>New Project Assignment</h1>
        </div>
        <div class="content">
          <p>Hello ${teamMember.name},</p>
          
          <p>You have been assigned to a new project. Here are the details:</p>
          
          <div class="assignment-details">
            <h3>${project.name}</h3>
            <p><strong>Client:</strong> ${project.client || 'Not specified'}</p>
            ${project.address ? `<p><strong>Project Address:</strong> ${project.address}</p>` : ''}
            ${project.city ? `<p><strong>City:</strong> ${project.city}</p>` : ''}
            ${project.postalCode ? `<p><strong>Postal Code:</strong> ${project.postalCode}</p>` : ''}
            <p><strong>Project Period:</strong> ${startDate} to ${endDate}</p>
            <p><strong>Your Work Days:</strong> ${workDays}</p>
            ${project.description ? `<p><strong>Project Description:</strong> ${project.description}</p>` : ''}
            ${assignmentData.notes ? `<p><strong>Assignment Notes:</strong> ${assignmentData.notes}</p>` : ''}
          </div>
          
          <div class="highlight">
            <p><strong>Please review your schedule and let us know if you have any questions.</strong></p>
          </div>
          
          <p>Best regards,<br>Your Team</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

const generateAssignmentEmailText = (assignmentData, teamMember, project) => {
  const startDate = new Date(assignmentData.startDate).toLocaleDateString();
  const endDate = new Date(assignmentData.endDate).toLocaleDateString();
  const workDays = assignmentData.workDays?.map(day => 
    new Date(day).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    })
  ).join(', ') || 'All days';

  return `
New Project Assignment

Hello ${teamMember.name},

You have been assigned to a new project. Here are the details:

Project: ${project.name}
Client: ${project.client || 'Not specified'}
${project.address ? `Project Address: ${project.address}` : ''}
${project.city ? `City: ${project.city}` : ''}
${project.postalCode ? `Postal Code: ${project.postalCode}` : ''}
Project Period: ${startDate} to ${endDate}
Your Work Days: ${workDays}
${project.description ? `Project Description: ${project.description}` : ''}
${assignmentData.notes ? `Assignment Notes: ${assignmentData.notes}` : ''}

Please review your schedule and let us know if you have any questions.

Best regards,
Your Team
  `.trim();
};

export const sendAssignmentUpdateNotification = async (assignmentData, teamMember, project, isDateChange = false) => {
  try {
    const emailContent = {
      to: teamMember.email,
      subject: isDateChange ? `Assignment Updated: ${project.name}` : `Assignment Modified: ${project.name}`,
      html: generateAssignmentUpdateEmailHTML(assignmentData, teamMember, project, isDateChange),
      text: generateAssignmentUpdateEmailText(assignmentData, teamMember, project, isDateChange)
    };
    
    // Send email using your existing SendGrid setup
    const apiUrl = `${DEVELLO_API_ORIGIN}/api/send-assignment-email`;
      
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: teamMember.email,
        subject: emailContent.subject,
        html: emailContent.html,
        text: emailContent.text
      }),
      name: 'email:assignmentUpdate',
      timeoutMs: 45000,
      retries: 1,
      supabaseClient: supabase,
      requireAuth: false,
      retryAuth: true,
    });

    if (!response.ok) {
      throw new Error(response.userMessage || 'Email sending failed');
    }
    const result = response.data;
    devLog('Assignment update notification sent:', result);
    
    return { success: true, message: 'Update notification sent successfully' };
  } catch (error) {
    devError('Error sending assignment update notification:', error);
    return { success: false, message: 'Failed to send update notification' };
  }
};

const generateAssignmentUpdateEmailHTML = (assignmentData, teamMember, project, isDateChange) => {
  const startDate = new Date(assignmentData.startDate).toLocaleDateString();
  const endDate = new Date(assignmentData.endDate).toLocaleDateString();
  const workDays = assignmentData.workDays?.map(day => 
    new Date(day).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    })
  ).join(', ') || 'All days';

  const updateType = isDateChange ? 'dates have been updated' : 'details have been modified';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #f59e0b; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9fafb; padding: 20px; border-radius: 0 0 8px 8px; }
        .assignment-details { background: white; padding: 15px; margin: 15px 0; border-radius: 5px; border-left: 4px solid #f59e0b; }
        .highlight { background: #fef3c7; padding: 10px; border-radius: 5px; margin: 10px 0; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Assignment Updated</h1>
        </div>
        <div class="content">
          <p>Hello ${teamMember.name},</p>
          
          <p>Your assignment for the following project ${updateType}:</p>
          
          <div class="assignment-details">
            <h3>${project.name}</h3>
            <p><strong>Client:</strong> ${project.client || 'Not specified'}</p>
            ${project.address ? `<p><strong>Project Address:</strong> ${project.address}</p>` : ''}
            ${project.city ? `<p><strong>City:</strong> ${project.city}</p>` : ''}
            ${project.postalCode ? `<p><strong>Postal Code:</strong> ${project.postalCode}</p>` : ''}
            <p><strong>Project Period:</strong> ${startDate} to ${endDate}</p>
            <p><strong>Your Work Days:</strong> ${workDays}</p>
            ${project.description ? `<p><strong>Project Description:</strong> ${project.description}</p>` : ''}
            ${assignmentData.notes ? `<p><strong>Assignment Notes:</strong> ${assignmentData.notes}</p>` : ''}
          </div>
          
          <div class="highlight">
            <p><strong>Please review the updated schedule and let us know if you have any questions.</strong></p>
          </div>
          
          <p>Best regards,<br>Your Team</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

const generateAssignmentUpdateEmailText = (assignmentData, teamMember, project, isDateChange) => {
  const startDate = new Date(assignmentData.startDate).toLocaleDateString();
  const endDate = new Date(assignmentData.endDate).toLocaleDateString();
  const workDays = assignmentData.workDays?.map(day => 
    new Date(day).toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    })
  ).join(', ') || 'All days';

  const updateType = isDateChange ? 'dates have been updated' : 'details have been modified';

  return `
Assignment Updated

Hello ${teamMember.name},

Your assignment for the following project ${updateType}:

Project: ${project.name}
Client: ${project.client || 'Not specified'}
${project.address ? `Project Address: ${project.address}` : ''}
${project.city ? `City: ${project.city}` : ''}
${project.postalCode ? `Postal Code: ${project.postalCode}` : ''}
Project Period: ${startDate} to ${endDate}
Your Work Days: ${workDays}
${project.description ? `Project Description: ${project.description}` : ''}
${assignmentData.notes ? `Assignment Notes: ${assignmentData.notes}` : ''}

Please review the updated schedule and let us know if you have any questions.

Best regards,
Your Team
  `.trim();
}; 